import { Files, X } from "lucide-react";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import api from "~/services/api";
import uploadFile from "~/functions/uploadPdf";

export async function processEnrollmentFile(file, documentId) {
  const originalName = file?.name || "file";
  const mime = file?.type || "";
  let toUpload = file;
  if (mime === "application/pdf" || /\.pdf$/i.test(originalName)) {
  } else if (
    mime.startsWith("image/") ||
    /\.(png|jpe?g|webp|bmp|gif)$/i.test(originalName)
  ) {
    const { PDFDocument } = await import("pdf-lib");
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.create();
    let embedded;
    if (mime === "image/png") {
      embedded = await pdfDoc.embedPng(new Uint8Array(arrayBuffer));
    } else {
      embedded = await pdfDoc.embedJpg(new Uint8Array(arrayBuffer));
    }
    const page = pdfDoc.addPage([embedded.width, embedded.height]);
    page.drawImage(embedded, {
      x: 0,
      y: 0,
      width: embedded.width,
      height: embedded.height,
    });
    const pdfBytes = await pdfDoc.save();
    const base = originalName.replace(/\.[^.]+$/, "");
    toUpload = new File([pdfBytes], `${base}.pdf`, {
      type: "application/pdf",
    });
  }
  const uploadedFile = await uploadFile(toUpload, "Enrollments");
  const normalizedName = (toUpload.name || originalName)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return {
    ...uploadedFile,
    key: uploadedFile.name,
    name: normalizedName,
    type: "pdf",
    document_id: documentId,
  };
}

const FileInputV3 = forwardRef(function FileInputV3(
  {
    document,
    isEditable,
    onPendingChange,
    enrollmentId,
    sponsorId,
    enrollmentdocuments,
    onDelete,
    onDeleteDirect,
    onUploaded,
    mode = "enrollment",
    savedFiles: savedFilesProp,
    pendingFiles: pendingFilesProp,
    onAddPending,
    onRemovePending,
    showChooseFile = true,
    replacedWarning: replacedWarningProp = false,
  },
  ref
) {
  const isDependent = mode === "dependent";

  const [localPendingFiles, setLocalPendingFiles] = useState([]);
  const [localReplacedWarning, setLocalReplacedWarning] = useState(false);
  const [pendingReplace, setPendingReplace] = useState(null);

  const replacedWarning = isDependent ? replacedWarningProp : localReplacedWarning;

  const savedFiles = isDependent
    ? savedFilesProp || []
    : (enrollmentdocuments || []).filter(
        (ed) => ed.document_id === document.id
      );

  const pendingFiles = isDependent ? pendingFilesProp || [] : localPendingFiles;

  useImperativeHandle(ref, () => ({
    documentId: document.id,
    async uploadPending(onFileUploaded) {
      if (!localPendingFiles.length) return;
      for (const { file } of localPendingFiles) {
        const processedFile = await processEnrollmentFile(file, document.id);
        await api.post(`/enrollmentdocuments`, {
          enrollment_id: enrollmentId,
          ...(sponsorId ? { sponsor_id: sponsorId } : {}),
          files: processedFile,
        });
        onFileUploaded?.();
      }
      setLocalPendingFiles([]);
      onUploaded?.();
    },
    hasPending() {
      return localPendingFiles.length > 0;
    },
    pendingCount() {
      return localPendingFiles.length;
    },
  }));

  function handleSelectFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    if (isDependent) {
      onAddPending?.(files);
    } else {
      if (!document.multiple) {
        if (savedFiles.length > 0) {
          setPendingReplace({ file: files[0] });
          return;
        }
        const alreadyHasFile = localPendingFiles.length > 0;
        setLocalPendingFiles([{ file: files[0], name: files[0].name }]);
        if (alreadyHasFile) {
          setLocalReplacedWarning(true);
          setTimeout(() => setLocalReplacedWarning(false), 3000);
        }
      } else {
        setLocalPendingFiles((prev) => [
          ...prev,
          ...files.map((f) => ({ file: f, name: f.name })),
        ]);
      }
    }
    onPendingChange?.();
  }

  function confirmReplace() {
    if (!pendingReplace) return;
    (onDeleteDirect ?? onDelete)?.(savedFiles[0].id);
    setLocalPendingFiles([{ file: pendingReplace.file, name: pendingReplace.file.name }]);
    setPendingReplace(null);
    onPendingChange?.();
  }

  function cancelReplace() {
    setPendingReplace(null);
  }

  function handleRemovePending(name) {
    if (isDependent) {
      onRemovePending?.(name);
    } else {
      setLocalPendingFiles((prev) => prev.filter((pf) => pf.name !== name));
    }
  }

  return (
    <div className="w-full px-4">
      <h1 className="w-full border-b pb-2 font-bold flex items-center gap-1">
        {document.title}
        {document.required && (
          <span className="text-mila_orange font-bold text-sm">*</span>
        )}
      </h1>
      {pendingReplace && (
        <div className="my-2 flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span>
            This field only allows one file. Do you want to replace the current
            file with <strong>{pendingReplace.file.name}</strong>?
          </span>
          <div className="flex flex-row gap-2">
            <button
              type="button"
              onClick={confirmReplace}
              className="rounded-md bg-mila_orange px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
            >
              Yes, replace
            </button>
            <button
              type="button"
              onClick={cancelReplace}
              className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-col items-start gap-3 w-full p-4 pt-2 pb-2">
        {showChooseFile && isEditable && (
          <div className="flex flex-row items-center gap-2 flex-wrap">
            <label className="flex-shrink-0 cursor-pointer bg-mila_orange text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:opacity-90 transition-opacity">
              Choose file
              <input
                type="file"
                className="hidden"
                multiple={document.multiple}
                onChange={handleSelectFiles}
                disabled={!isEditable}
              />
            </label>
            {replacedWarning && (
              <span className="text-xs text-amber-600">
                Only one file is allowed in this field — previous selection replaced.
              </span>
            )}
          </div>
        )}

        {!isEditable && savedFiles.length === 0 && pendingFiles.length === 0 && (
          <span className="text-xs text-gray-400 italic">No file uploaded.</span>
        )}

        {(savedFiles.length > 0 || pendingFiles.length > 0) && (
          <div className="w-full">
            <h3 className="w-full border-b pb-2 text-xs text-gray-500">
              Attached Files
            </h3>
            <div className="flex flex-col items-start gap-2 pt-2">
              {savedFiles.map((ed, index) => (
                <div key={index} className="flex flex-row items-center gap-2">
                  <a
                    href={ed?.file?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs"
                  >
                    <div className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-gray-100 hover:border-gray-300 truncate">
                      <Files size={16} className="flex-shrink-0" />
                      <span className="truncate">{ed.file.name}</span>
                    </div>
                  </a>
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => onDelete(ed.id)}
                      className="text-xs text-red-700 flex flex-row items-center gap-1 px-2 py-1 rounded-sm hover:bg-red-100"
                    >
                      <X size={12} /> Delete
                    </button>
                  )}
                </div>
              ))}
              {pendingFiles.map((pf, index) => (
                <div
                  key={`pending-${index}`}
                  className="flex flex-row items-center gap-2"
                >
                  <div className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-yellow-50 border-yellow-300 truncate text-xs">
                    <Files size={16} className="flex-shrink-0 text-yellow-600" />
                    <span className="truncate">{pf.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePending(pf.name)}
                    className="text-xs text-red-700 flex flex-row items-center gap-1 px-2 py-1 rounded-sm hover:bg-red-100"
                  >
                    <X size={12} /> Remove
                  </button>
                </div>
              ))}
              {pendingFiles.length > 0 && (
                <span className="text-xs text-yellow-700">
                  Save and Continue to save the uploads
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default FileInputV3;
